/**
 * Teen 21-Day Challenge Page
 * Matches the main app's challenge layout with expandable day steps.
 * Uses TeenThemeContext for customizable look and feel.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Rocket,
  Check,
  Lock,
  Star,
  Flame,
  Trophy,
  Zap,
  X,
  Heart,
  Eye,
  Sparkles,
  Target,
  ChevronDown,
  ChevronUp,
  CheckCircle2
} from "lucide-react";
import axios from "axios";
import JourneyCompleteCelebration from "../../components/Teen/JourneyCompleteCelebration";
import { TeenThemeProvider, useTeenTheme } from "../../contexts/TeenThemeContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons and colors
const CATEGORY_CONFIG = {
  confidence: { icon: Star, color: "from-purple-500 to-pink-500", bgColor: "bg-purple-500", textColor: "text-purple-400" },
  kindness: { icon: Heart, color: "from-rose-500 to-orange-500", bgColor: "bg-rose-500", textColor: "text-rose-400" },
  reflection: { icon: Eye, color: "from-blue-500 to-cyan-500", bgColor: "bg-blue-500", textColor: "text-blue-400" },
  focus: { icon: Target, color: "from-amber-500 to-orange-500", bgColor: "bg-amber-500", textColor: "text-amber-400" },
  emotional: { icon: Sparkles, color: "from-emerald-500 to-teal-500", bgColor: "bg-emerald-500", textColor: "text-emerald-400" }
};

// Day Step Component - matches main app style
const DayStep = ({ 
  day, 
  challenge, 
  isCompleted, 
  isCurrent, 
  isLocked, 
  onComplete, 
  savedReflection,
  isExpanded,
  onToggle
}) => {
  const [reflectionText, setReflectionText] = useState("");
  const [completing, setCompleting] = useState(false);
  const [showReflectionInput, setShowReflectionInput] = useState(false);

  const config = CATEGORY_CONFIG[challenge.category] || CATEGORY_CONFIG.confidence;
  const CategoryIcon = config.icon;

  const handleComplete = async () => {
    if (isCompleted || isLocked) return;
    setCompleting(true);
    await onComplete(challenge.day, reflectionText || null);
    setCompleting(false);
    setShowReflectionInput(false);
    setReflectionText("");
  };

  return (
    <motion.div
      initial={false}
      animate={{ opacity: isLocked ? 0.5 : 1 }}
      className={`border rounded-2xl overflow-hidden transition-all ${
        isCurrent && !isCompleted 
          ? 'border-purple-500/50 bg-purple-500/5' 
          : isCompleted
            ? 'border-emerald-500/30 bg-emerald-500/5'
            : 'border-white/10 bg-white/5'
      }`}
    >
      {/* Header */}
      <button
        onClick={onToggle}
        disabled={isLocked}
        className={`w-full p-4 flex items-center gap-4 text-left ${isLocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        data-testid={`day-step-${challenge.day}`}
      >
        {/* Status Icon */}
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isCompleted 
            ? 'bg-emerald-500 text-white' 
            : isCurrent 
              ? `bg-gradient-to-br ${config.color} text-white`
              : 'bg-white/10 text-white/50'
        }`}>
          {isCompleted ? (
            <CheckCircle2 size={24} />
          ) : isLocked ? (
            <Lock size={18} />
          ) : (
            <span className="font-bold text-lg">{challenge.day}</span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className={`font-bold ${isCompleted ? 'text-emerald-400' : 'text-white'}`}>
              Day {challenge.day}: {challenge.title}
            </p>
            <span className={`px-2 py-0.5 rounded-full text-xs ${config.bgColor}/20 ${config.textColor}`}>
              {challenge.category}
            </span>
          </div>
          {!isExpanded && (
            <p className="text-sm text-white/60 truncate">
              {challenge.description}
            </p>
          )}
        </div>

        {/* XP Badge */}
        <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-semibold">
          <Zap size={12} />
          {challenge.xp}
        </div>

        {/* Expand Icon */}
        {!isLocked && (
          <div className="text-white/50">
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        )}
      </button>

      {/* Expanded Content */}
      <AnimatePresence>
        {isExpanded && !isLocked && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-0">
              <div className="pl-16 space-y-4">
                {/* Description */}
                <p className="text-white/70 leading-relaxed">
                  {challenge.description}
                </p>

                {/* Challenge Box */}
                <div className={`p-4 rounded-xl bg-gradient-to-br ${config.color}`}>
                  <p className="text-white/80 text-sm font-medium mb-2">TODAY'S CHALLENGE</p>
                  <p className="text-white font-medium leading-relaxed">
                    {challenge.challenge}
                  </p>
                </div>

                {/* Guidance */}
                {challenge.guidance && (
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                    <p className="text-white/80 text-sm font-medium mb-2">GUIDANCE</p>
                    <p className="text-white/70 leading-relaxed">
                      {challenge.guidance}
                    </p>
                  </div>
                )}

                {/* Reflection Prompt */}
                {challenge.reflection_prompt && (
                  <div className="p-4 bg-purple-500/10 rounded-xl border border-purple-500/20">
                    <p className="text-purple-400 text-sm font-medium mb-1">REFLECT:</p>
                    <p className="text-white/70 italic">
                      {challenge.reflection_prompt}
                    </p>
                  </div>
                )}

                {/* Saved Reflection */}
                {savedReflection && (
                  <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
                    <p className="text-emerald-400 text-sm font-medium mb-1">YOUR REFLECTION:</p>
                    <p className="text-white/80">"{savedReflection}"</p>
                  </div>
                )}

                {/* Complete Button / Reflection Input */}
                {!isCompleted && (
                  <>
                    {showReflectionInput ? (
                      <div className="space-y-3">
                        <textarea
                          value={reflectionText}
                          onChange={(e) => setReflectionText(e.target.value)}
                          placeholder="Write your reflection here (optional)..."
                          className="w-full h-24 p-3 rounded-xl border border-white/20 bg-white/5 text-white placeholder-white/30 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                          data-testid="reflection-input"
                        />
                        <div className="flex gap-2">
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={handleComplete}
                            disabled={completing}
                            className="flex-1 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold disabled:opacity-50"
                            data-testid="complete-day-btn"
                          >
                            {completing ? "Completing..." : "I Did It!"}
                          </motion.button>
                          <button
                            onClick={() => setShowReflectionInput(false)}
                            className="px-4 py-3 rounded-xl bg-white/10 text-white/70 hover:bg-white/20"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setShowReflectionInput(true)}
                        className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold shadow-lg shadow-purple-500/30"
                      >
                        Complete This Challenge
                      </motion.button>
                    )}
                  </>
                )}

                {/* Completed Badge */}
                {isCompleted && (
                  <div className="flex items-center gap-2 py-3 text-emerald-400">
                    <CheckCircle2 size={20} />
                    <span className="font-semibold">Challenge Completed!</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const TeenChallengeContent = () => {
  const navigate = useNavigate();
  const { theme, currentPalette } = useTeenTheme();
  const [challenge, setChallenge] = useState(null);
  const [progress, setProgress] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);
  const [showJourneyCelebration, setShowJourneyCelebration] = useState(false);
  
  const getToken = () => localStorage.getItem("reclaim_token");
  
  // Theme colors with fallbacks
  const colors = {
    base: theme?.colors?.base || '#0f0f23',
    primary: currentPalette?.primary || theme?.colors?.primary || '#818CF8',
    accent: currentPalette?.accent || theme?.colors?.accent || '#F472B6',
    textPrimary: theme?.colors?.textPrimary || '#ffffff',
    textSecondary: theme?.colors?.textSecondary || 'rgba(255,255,255,0.6)',
    textMuted: theme?.colors?.textMuted || 'rgba(255,255,255,0.4)',
  };

  useEffect(() => {
    loadChallenge();
    loadProgress();
  }, []);

  const loadChallenge = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/teen/challenge`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setChallenge(res.data);
    } catch (err) {
      console.error("Error loading challenge:", err);
    }
  };

  const loadProgress = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/teen/challenge/progress`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setProgress(res.data);
      
      // Auto-expand current day
      if (res.data.current_day && !expandedDay) {
        setExpandedDay(res.data.current_day);
      }
    } catch (err) {
      console.error("Error loading progress:", err);
    }
  };

  const handleStartChallenge = async () => {
    try {
      const res = await axios.post(
        `${API_URL}/api/teen/challenge/start`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setProgress(res.data.progress);
      setExpandedDay(1);
    } catch (err) {
      console.error("Error starting challenge:", err);
    }
  };

  const handleCompleteDay = async (day, reflection) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/teen/challenge/day/${day}/complete`,
        { day, reflection },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      // Check if this completes the entire journey (day 21)
      if (day === 21) {
        setShowJourneyCelebration(true);
      }
      
      loadProgress();
      
      // Auto-expand next day
      if (day < 21) {
        setExpandedDay(day + 1);
      }
      
    } catch (err) {
      console.error("Error completing day:", err);
    }
  };

  const handleRestartJourney = async () => {
    try {
      const res = await axios.post(
        `${API_URL}/api/teen/challenge/restart`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      if (res.data.success) {
        setProgress(res.data.progress);
        setShowJourneyCelebration(false);
        setExpandedDay(1);
        loadChallenge();
      }
    } catch (err) {
      console.error("Error restarting journey:", err);
    }
  };

  if (!challenge) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ background: colors.base }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
        >
          <Rocket size={40} style={{ color: colors.primary }} />
        </motion.div>
      </div>
    );
  }

  // Not started view
  if (!progress?.started_at) {
    return (
      <div 
        className="min-h-screen flex flex-col"
        style={{ background: colors.base }}
      >
        <div className="p-4">
          <button
            onClick={() => navigate("/teen")}
            className="flex items-center gap-2"
            style={{ color: colors.textSecondary }}
          >
            <ArrowLeft size={20} />
          </button>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative mb-8"
          >
            <div 
              className="w-32 h-32 rounded-3xl flex items-center justify-center shadow-2xl"
              style={{ 
                background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
                boxShadow: `0 25px 50px -12px ${colors.primary}40`
              }}
            >
              <Rocket size={56} className="text-white" />
            </div>
          </motion.div>

          <h1 
            className="text-3xl font-bold mb-3"
            style={{ color: colors.textPrimary }}
          >
            {challenge.title}
          </h1>
          <p 
            className="mb-8 max-w-sm"
            style={{ color: colors.textSecondary }}
          >
            {challenge.description}
          </p>

          <div className="flex gap-8 mb-8">
            <div className="text-center">
              <div 
                className="text-3xl font-bold"
                style={{ color: colors.primary }}
              >21</div>
              <div style={{ color: colors.textMuted }} className="text-sm">Days</div>
            </div>
            <div className="text-center">
              <div 
                className="text-3xl font-bold"
                style={{ color: colors.accent }}
              >5</div>
              <div style={{ color: colors.textMuted }} className="text-sm">Categories</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-amber-400">3K+</div>
              <div style={{ color: colors.textMuted }} className="text-sm">XP to Earn</div>
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleStartChallenge}
            className="px-10 py-4 rounded-2xl text-white font-bold text-lg shadow-xl flex items-center gap-3"
            style={{ 
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
              boxShadow: `0 10px 40px -10px ${colors.primary}50`
            }}
            data-testid="start-challenge-btn"
          >
            <Rocket size={24} />
            <span>Start My Journey</span>
          </motion.button>
        </div>
      </div>
    );
  }

  // Main journey view with expandable days
  return (
    <div 
      className="min-h-screen"
      style={{ background: colors.base }}
    >
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <button
          onClick={() => navigate("/teen")}
          style={{ color: colors.textSecondary }}
        >
          <ArrowLeft size={20} />
        </button>
        
        <h1 className="text-white font-semibold">21-Day Journey</h1>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/teen/history")}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            title="Journey History"
          >
            <Trophy size={16} className="text-amber-400" />
          </button>
          <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20">
            <Zap size={14} className="text-amber-400" />
            <span className="text-amber-300 font-semibold text-sm">{progress?.total_xp || 0}</span>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="px-6 mb-4">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-white/60">Progress</span>
          <span className="text-white/80">{progress?.completed_days?.length || 0}/21 days</span>
        </div>
        <div className="h-3 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${((progress?.completed_days?.length || 0) / 21) * 100}%` }}
            transition={{ duration: 0.5 }}
            className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
          />
        </div>
      </div>

      {/* Streak */}
      {progress?.streak > 0 && (
        <div className="px-6 mb-4">
          <div className="p-3 rounded-xl bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 flex items-center gap-3">
            <Flame size={24} className="text-orange-400" />
            <div>
              <div className="text-white font-bold">{progress.streak} Day Streak!</div>
              <div className="text-white/60 text-sm">Keep it going!</div>
            </div>
          </div>
        </div>
      )}

      {/* Journey Complete Banner */}
      {progress?.completed_days?.length === 21 && (
        <div className="px-6 mb-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowJourneyCelebration(true)}
            className="w-full p-4 rounded-xl bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500 shadow-xl shadow-orange-500/30 flex items-center gap-4"
            data-testid="view-celebration-btn"
          >
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <Trophy size={24} className="text-white" />
            </div>
            <div className="flex-1 text-left">
              <div className="text-white font-bold">Journey Complete!</div>
              <div className="text-white/80 text-sm">Tap to celebrate your achievement</div>
            </div>
            <Sparkles size={20} className="text-white/80" />
          </motion.button>
        </div>
      )}

      {/* Day Steps */}
      <div className="px-6 pb-8 space-y-3">
        {challenge.days.map((day) => {
          const isCompleted = progress?.completed_days?.includes(day.day);
          const isCurrent = day.day === progress?.current_day;
          const isLocked = day.day > (progress?.current_day || 1);
          const savedReflection = progress?.reflections?.[String(day.day)];
          
          return (
            <DayStep
              key={day.day}
              day={day.day}
              challenge={day}
              isCompleted={isCompleted}
              isCurrent={isCurrent}
              isLocked={isLocked}
              onComplete={handleCompleteDay}
              savedReflection={savedReflection}
              isExpanded={expandedDay === day.day}
              onToggle={() => setExpandedDay(expandedDay === day.day ? null : day.day)}
            />
          );
        })}
      </div>

      {/* Journey Complete Celebration */}
      <JourneyCompleteCelebration
        isOpen={showJourneyCelebration}
        onClose={() => setShowJourneyCelebration(false)}
        stats={{
          total_xp: progress?.total_xp || 0,
          streak: progress?.streak || 0,
          reflections_count: Object.keys(progress?.reflections || {}).length
        }}
        onShare={() => navigate("/teen/share")}
        onRestart={handleRestartJourney}
      />
    </div>
  );
};

// Main component wraps with theme provider
const TeenChallengePage = () => {
  return (
    <TeenThemeProvider>
      <TeenChallengeContent />
    </TeenThemeProvider>
  );
};

export default TeenChallengePage;
